import {
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	ViewChild,
	NgZone,
	ViewChildren,
	QueryList,
	Input,
} from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import {
	ApplicationContext,
	CompanyService,
	ComponentBase,
	DeploymentContext,
	IframeResizerService,
	SearchResolverResult,
	SearchService,
	Summary,
	TitleAndMetadata,
	WebAnalyticsService,
} from '@Common';
import { SortControlComponent } from './components/sort-control/sort-control.component.js';
import { LargeDownloadModalComponent } from './components/large-download-modal/large-download-modal.component.js';
import {
	Asset,
	AssetKey,
	Company,
	DocumentGeneration,
	LocalizedTextIds,
	Mode,
	SortDetails,
	TagWithCount,
} from 'company-finder-common';
import { AuthnService, BreadcrumbsService } from '@Common';
import { CompanySummaryComponent } from './components/company-summary/company-summary.component.js';
import { iframeResizer } from '@iframe-resizer/child';

@Component({
	selector: 'search-results',
	templateUrl: './search-results.component.html',
	styleUrls: ['./search-results.component.scss'],
	standalone: false,
})
export class SearchResultsComponent
	extends ComponentBase
	implements OnDestroy, OnInit
{
	public companies: Company[] = [];
	public assets: Asset[] = [];

	public get loadedCount(): number {
		switch (this.mode) {
			case Mode.Company:
				return this.companies.length;
			case Mode.Asset:
				return this.assets.length;
		}
	}

	public get totalItems(): number {
		switch (this.mode) {
			case Mode.Company:
				return this.totalCompanies;
			case Mode.Asset:
				return this.totalAssets;
		}
	}

	public set totalItems(val: number) {
		switch (this.mode) {
			case Mode.Company:
				this.totalCompanies = val;
				break;
			case Mode.Asset:
				this.totalAssets = val;
				break;
		}
	}

	private totalCompanies: number;
	private totalAssets: number;

	public comprehensiveSummary: Summary;
	@ViewChild('sortControl', { static: true })
	public sortControl: SortControlComponent;

	@ViewChild('largeDownloadModal')
	public modal: LargeDownloadModalComponent;

	@ViewChildren(CompanySummaryComponent)
	protected companySummaries: QueryList<CompanySummaryComponent>;
	protected get firstSummaryElement(): CompanySummaryComponent {
		return this.companySummaries?.first;
	}
	protected get footerHeight(): number {
		return this._deploymentContext.footerHeight;
	}
	protected get summaryHeight(): number {
		return this.firstSummaryElement?.height ?? 0;
	}

	@Input()
	public set searchResults(searchResults: SearchResolverResult) {
		// A resolved result set replaces the list wholesale, so the per-index
		// bookkeeping from the previous set no longer applies. Note that a
		// filter/sort change re-resolves this route rather than re-creating the
		// component (onSameUrlNavigation: 'reload' + runGuardsAndResolvers:
		// 'always' + bindToComponentInputs), so without this reset a new result
		// set landing on an already-completed $index makes loadCompleted()
		// return early, leaving isLoadCompleted false and waitingOnScroll true.
		// That kills infinite scroll and leaves the resizer disabled, but only
		// when hosted in an iframe, since the stand-alone window:scroll handler
		// doesn't consult either flag.
		this.completedLoads = [];
		this.isLoadCompleted = false;
		// Park the resizer while the DOM is replaced, for the same reason as
		// ngOnInit; loadCompleted() re-enables it once the page is stable.
		if (this.inCompanyMode) {
			this.disableResizer();
		}

		this.companies = searchResults.companies;
		this.assets = searchResults.assets;
		this.totalCompanies = searchResults.totalCompanies;

		this.totalAssets = searchResults.totalAssets;
		this.tagCounts = searchResults.tagCounts;
		this.comprehensiveSummary = searchResults.summary;
	}

	public showMenu = false;
	public isShowingTooltip = false;
	public tagCounts: TagWithCount[];
	public debounceScroll = true;
	public waitingOnScroll = false;
	public get showAllCompanies(): boolean {
		return this.searchService.isAllItems;
	}
	public showLargeDownloadModal = false;
	public isLoadCompleted = false;
	public completedLoads = [];

	private docConfig: DocumentGeneration;
	private scrollY: number;

	public constructor(
		dc: DeploymentContext,
		private appContext: ApplicationContext,
		private authnService: AuthnService,
		private breadcrumbsService: BreadcrumbsService,
		private companyService: CompanyService,
		private searchService: SearchService,
		private webAnalyticsService: WebAnalyticsService,
		private scrollingService: IframeResizerService,
		private ngZone: NgZone,
		private titleAndMetadata: TitleAndMetadata,
		router: Router
	) {
		super(dc);
		this.docConfig = this._deploymentContext.documentGenerationConfig;

		router.events.subscribe((event) => {
			// If we are navigating away, save the scroll position
			if (
				event instanceof NavigationStart &&
				event.url.indexOf('/search-results') < 0
			) {
				this.searchService.initialFetchSize = this.companies.length;
				breadcrumbsService.setSearchPageOffset(this.getScrollY());
			} else if (
				event instanceof NavigationEnd &&
				event.url.indexOf('/search-results') >= 0
			) {
				// coming back, restore the scroll
				this.waitingOnScroll = true;
			}
		});
	}

	public get badSearchPredicate(): string {
		return this.searchService.badSearchPredicate;
	}

	public async exportCompaniesAsCsv(): Promise<void> {
		const companiesForExport = await this.companyService.companiesForExport(
			this.searchService.currentSearchPredicate,
			this.searchService.filter
		);

		this.appContext.saveDocument(
			this.Loc(LocalizedTextIds.ExportFileName),
			companiesForExport,
			'csv'
		);
	}

	public get isDownloadEnabled(): boolean {
		return this.featureSwitches.enableDownload || false;
	}

	public get isDownloadDocumentEnabled(): boolean {
		return this.docConfig.documentDownloadEnabled || false;
	}

	public get isInternalView(): boolean {
		return this.authnService.isAuthenticated;
	}

	public async downloadWordDocument(): Promise<void> {
		this.showMenu = false;
		const response = await this.companyService.getWordDataForSearch(
			this.searchService.currentSearchPredicate,
			this.searchService.filter
		);
		this.appContext.saveDocument(
			this.Loc(LocalizedTextIds.SearchResultsPortfolioSearch),
			response,
			'docx'
		);

		// FUTURE: Would be nice to know how many companies were in the set exported, but that is buried a bit deeper
		this.webAnalyticsService.trackEvent('export-search-results-as-word', {
			category: 'Export',
			label: this.searchService.getSearchTermForAnalytics(),
		});
	}

	public async downloadPdfClicked(): Promise<void> {
		// PDF generation is a time-consuming operation, and exceeds a reasonable wait time,
		// and exceeds a timeout enforced by the deployment infrastructure, so apply a limit here.
		const maxCompanyCountAllowedForPdf =
			this.docConfig.maxCompanyCountAllowedForPdf;
		if (this.totalItems > maxCompanyCountAllowedForPdf) {
			this.showLargeDownloadModal = true;
			return;
		}
		this.showMenu = false;
		await this.downloadPdf();
	}

	public async downloadPdf(): Promise<void> {
		const shouldPage =
			this.totalItems > this.docConfig.maxCompanyCountAllowedForPdf;

		const sharedTimestamp = shouldPage ? Date.now() : undefined;
		for (
			let i = 0;
			i < this.totalItems;
			i += this.docConfig.maxCompanyCountAllowedForPdf
		) {
			const response = await this.companyService.getPdfDataForSearch(
				this.searchService.currentSearchPredicate,
				this.searchService.filter,
				i,
				this.docConfig.maxCompanyCountAllowedForPdf
			);

			const pageSuffix = shouldPage
				? ` part ${i / this.docConfig.maxCompanyCountAllowedForPdf + 1}`
				: undefined;

			this.appContext.saveDocument(
				this.Loc(LocalizedTextIds.SearchResultsPortfolioSearch),
				response,
				'pdf',
				pageSuffix,
				sharedTimestamp
			);
		}

		// FUTURE: Would be nice to know how many companies were in the set exported, but that is buried a bit deeper
		this.webAnalyticsService.trackEvent('export-search-results-as-pdf', {
			category: 'Export',
			label: this.searchService.getSearchTermForAnalytics(),
		});
	}

	public async closeLargePdfModal(shouldDownload: boolean): Promise<void> {
		this.showLargeDownloadModal = false;
		if (shouldDownload) {
			this.downloadPdf();
		}
	}

	public ngOnInit(): void {
		// Let the hosting page preserve its default title for the search results page, but set a unique title
		// for Navigator as a hosted iframe to have better web analytics.
		this.titleAndMetadata.setPageTitle(
			`${this._deploymentContext.Loc(
				LocalizedTextIds.SearchResultsPortfolioSearch
			)}`
		);

		// If the resizer is enabled when the ngFor adds the
		// companies one at a time to the DOM it will cause
		// performance issues and hang the page. Instead, we
		// disable the resizer while the DOM is manipulated,
		// then re-enable it when the 'last' company summary
		// is on the screen so the resizer only has to manage
		// a stable page.
		if (this.inCompanyMode) {
			this.disableResizer();
		}

		if (this._deploymentContext.hosted()) {
			this.addSubscription(
				this.scrollingService.iframeSubject.subscribe((event) =>
					this.handleScrollEvent(event)
				)
			);
		}
	}

	public sortAsset(sort: SortDetails) {
		this.searchService.filter.sort = sort;
		this.searchService.drilldownSubject.next(Mode.Asset);
		this.webAnalyticsService.trackEvent('sort-asset-search-results', {
			label: sort.type,
			value: this.searchService.totalCount,
		});
	}

	public ngOnDestroy(): void {
		this._subscriptions?.unsubscribe();
		this._subscriptions = undefined;
		super.ngOnDestroy();
	}

	// ADJQ-93:  Support infinite scroll of result set at the page level as stand-alone app.
	@HostListener('window:scroll')
	public onScroll(): void {
		// Per: https://techstacker.com/posts/gGyGTHysrPuuJnNBk/vanilla-javascript-detect-when-user-scrolled-to-the-bottom
		// The original site is defunct - I beleive the same content is at https://techstacker.com/javascript-detect-when-scrolled-to-bottom/
		const atBottom =
			window.innerHeight + window.scrollY >=
			window.document.body.offsetHeight;
		if (atBottom && this.debounceScroll) {
			this.debounceScroll = false;
			this.loadNextPage();
		}
		if (!atBottom) {
			this.debounceScroll = true;
		}
	}

	public setShowMenu(ev: MouseEvent): void {
		ev.preventDefault();
		ev.stopPropagation();

		this.showMenu = true;
	}

	public showTooltip(): void {
		this.isShowingTooltip = true;
	}

	public hideTooltip(): void {
		this.isShowingTooltip = false;
	}

	@HostListener('window:click')
	protected onClick(): void {
		this.showMenu = false;
	}

	public loadCompleted(i: number): void {
		if (this.completedLoads[i]) {
			return;
		}

		this.completedLoads[i] = this.isLoadCompleted = true;
		this.enableResizer();

		if (this.waitingOnScroll) {
			this.waitingOnScroll = false;
			this.scroll();
		}
	}

	private scroll() {
		this._deploymentContext.scrollVertical(
			this.breadcrumbsService.searchPageOffset ?? 0
		);
	}

	// private methods
	private async loadNextPage() {
		if (this.loadedCount >= this.totalItems) {
			return;
		}

		this.disableResizer();
		this.isLoadCompleted = false;

		switch (this.mode) {
			case Mode.Company:
				({ items: this.companies, totalItems: this.totalItems } =
					await this.searchService.getCompanies(this.loadedCount));
				break;
			case Mode.Asset:
				({ items: this.assets, totalItems: this.totalItems } =
					await this.searchService.getAssets(this.loadedCount));
				break;
		}

		this.webAnalyticsService.trackEvent('paged-search-results', {
			label: `mode ${this.mode} - sort ${this.searchService.filter.sort} - had ${this.loadedCount}`,
			value: this.totalItems,
		});
	}

	private handleScrollEvent(windowProperties: iframeResizer.ParentProps) {
		this.scrollY = windowProperties.viewport.pageTop;
		const scrollPadding =
			(this.searchService.PAGE_SIZE / 3) * this.summaryHeight;

		// When the bottom of the page (excluding the footer height) is less than
		// the height of the page plus 1/3 of the summaries we load in a page.
		// trigger the scroll. In effect, with a PAGE_SIZE of 20, this should
		// trigger a scroll when there are ~7 companies plus the footer
		// below the iframe.
		const nearBottom =
			windowProperties.iframe.bottom - this.footerHeight <=
			windowProperties.viewport.height + scrollPadding;
		if (!this.waitingOnScroll && this.isLoadCompleted && nearBottom) {
			// Since this is invoked via subscribe, we need to invoke ngZone
			// so we don't lose change tracking
			this.ngZone.run(() => {
				this.loadNextPage();
			});
		}
	}

	private getScrollY(): number {
		// If hosted, our scrollY will be set by handleScrollEvent(), else use the window's
		return this.scrollY || window.scrollY;
	}

	public get availableAssetColumns(): AssetKey[] {
		return [
			'name',
			'companyName',
			'sector',
			'focusArea',
			'therapeuticArea',
			'indication',
			'target',
			'routeOfAdministration',
			'developmentStage',
			'clinicalOperatingCountry',
			'clinicalTrialNctNumber',
		];
	}

	public get searchTitle(): string {
		return this.showAllCompanies
			? this.Loc(LocalizedTextIds.AllCompanies, this.modePluralName)
			: this.Loc(LocalizedTextIds.SearchResultsSearch);
	}

	public get showCount(): boolean {
		return !this.featureSwitches.restrictToSearchResultsPage;
	}

	public get downloadPDF(): string {
		return this.Loc(LocalizedTextIds.DownloadPDF);
	}

	public get downloadWord(): string {
		return this.Loc(LocalizedTextIds.DownloadWord);
	}

	public get downloadCSV(): string {
		return this.Loc(LocalizedTextIds.DownloadCSV);
	}

	public get searchResultsMayTake(): string {
		return this.Loc(LocalizedTextIds.SearchResultsMayTake);
	}

	public get sorry(): string {
		return this.Loc(LocalizedTextIds.SearchResultsSorry);
	}

	public get sorryEnd(): string {
		return this.Loc(LocalizedTextIds.SearchResultsSorryEnd);
	}

	public get onlyShowingCompanies(): string {
		return this.Loc(LocalizedTextIds.SearchResultsOnlyShowingCompanies);
	}

	public get download(): string {
		return this.Loc(LocalizedTextIds.Download);
	}
}
